import React from 'react'
import { useDispatch } from 'react-redux'
import { t } from 'ttag'

import { toInterview } from 'ducks/router'
import { showModal } from 'ducks/ux'
import { SwitchAction } from './components'
import { Feature, RequireRole } from 'components/auth/features'
import type { FeatureKey, RoleKey } from 'generated/features'

// an action shown only when the account has the feature and the user has the role
type Requirement = { feature?: FeatureKey; role?: RoleKey }

const gate = (
  { feature, role }: Requirement = {},
  element: React.ReactElement,
) => {
  const withRole = role ? (
    <RequireRole role={role}>{element}</RequireRole>
  ) : (
    element
  )
  return feature ? <Feature name={feature}>{withRole}</Feature> : withRole
}

// utility functions
type Selection = { pks?: unknown[] | null }

const anySelected = ({ pks }: Selection) => (pks ?? []).length > 0

const oneSelected = ({ pks }: Selection) => (pks ?? []).length === 1

const noneSelected = (props: Selection) => !anySelected(props)

const notOneSelected = (props: Selection) => !oneSelected(props)

// standard action definitions
// name: {
//  reduxAction: [redux action creator function]
//  icon: Icon component to show
//  label: title of action
//  helpText: description of action
//  disabled: action button disabled
//  kebab: action item is contained in a kebab menu
//
// }
const modelActions = verb =>
  ({
    newCandidate: {
      reduxAction: ({ project }) =>
        showModal('EditCandidate', { pk: 'add', project }),
      icon: 'userPlus',
      label: t`Add candidate`,
      helpText: t`Add an candidate to the project.`,
    },
    editCandidate: {
      reduxAction: ({ pks }) => showModal('EditCandidate', { pk: pks[0] }),
      icon: 'edit',
      label: t`Edit`,
      helpText: t`Edit candidate profile.`,
    },
    save: {
      reduxAction: ({ data = {}, model, pks }) =>
        model.patchStart({ data, pk: pks[0] }),
      icon: 'save',
      label: t`Save`,
      helpText: t`Save current changes`,
      disabled: ({ pks = [] }) => !!pks?.length,
    },
    reset: {
      reduxAction: ({ model, pks }) => model.reset({ pk: pks[0] }),
      icon: 'undo',
      label: t`Reset`,
      helpText: t`Undo all changes since last save`,
    },
    create: {
      reduxAction: ({ model, tab }) => model.toCreate(tab),
      icon: 'plusCircle',
      label: ({ model }) => {
        const word = model.pluralize(1)
        return t`New ${word}`
      },
      helpText: ({ model }) => {
        const word = model.pluralize(1)
        return t`Create new ${word}`
      },
    },
    invite: {
      reduxAction: ({ pks, project }) =>
        showModal('SendInvite', { pks, project }),
      icon: 'mail',
      label: t`Invite`,
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Send invitation mail to ${n} ${word}`
          : t`No ${word} selected for invitation mail.`
      },
      disabled: noneSelected,
    },
    approve: {
      reduxAction: ({ pks }) => showModal('SelectForInterview', { pks }),
      icon: 'userCheck',
      label: t`Move to interview`,
      helpText: t`Select candidates for interview`,
      disabled: noneSelected,
    },
    report: {
      reduxAction: ({ pks = [] }) =>
        showModal('CandidateScores', { pk: pks[0] }),
      icon: 'file',
      label: t`Candidate report`,
      helpText: t`Show the candidate test score report`,
      disabled: notOneSelected,
    },
    interview: {
      reduxAction: ({ interviewId, featureFlag, pks }) =>
        featureFlag
          ? toInterview({ uuid: interviewId })
          : showModal('PerformInterview', { pk: pks[0] }),
      icon: 'message',
      label: t`Interview`,
      helpText: t`Conduct interview with candidate`,
      disabled: notOneSelected,
    },
    interviewReport: {
      reduxAction: ({ interviewId }) => toInterview({ uuid: interviewId }),
      icon: 'message',
      label: t`Interview report`,
      helpText: t`Open the candidate's interview report`,
      disabled: notOneSelected,
    },
    move: {
      reduxAction: ({ pks }) => showModal('MoveCandidates', { pks }),
      icon: 'move',
      label: t`Move`,
      helpText: t`Move candidates to selection step or to a different project`,
      disabled: noneSelected,
      requires: { feature: 'moveCandidates' } satisfies Requirement,
    },
    trash: {
      reduxAction: ({ model, pks }) => model.trash({ pks }),
      icon: 'trash',
      label: t`Delete`,
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Delete ${n} selected ${word}`
          : t`No ${word} selected for deletion`
      },
      warning: true,
      disabled: noneSelected,
    },
    import: {
      reduxAction: ({ model, pks }) => showModal('ImportSheet', {}),
      icon: 'importFile',
      label: t`Import`,
      helpText: t`Import candidate list from a spreadsheet file`,
    },
    export: {
      reduxAction: ({ model, pks }) =>
        showModal('ExportSheet', { model: model.endpoint, pks }),
      label: t`Export`,
      icon: 'export',
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Export ${n} selected ${word} to spreadsheet`
          : t`No ${word} selected for export`
      },
      disabled: noneSelected,
    },
  })[verb]

const actionFactory =
  (verb, defaultProps = { pks: [] }, ActionComponent = SwitchAction) =>
  ownProps => {
    const dispatch = useDispatch()
    const mergedProps = { ...defaultProps, ...ownProps }
    if (mergedProps.pk && !mergedProps.pks) {
      mergedProps.pks = [mergedProps.pk]
    }
    // Object.entries throws for an unknown verb, as R.map did
    const factoryProps = Object.fromEntries(
      Object.entries(modelActions(verb)).map(([key, value]) => [
        key,
        typeof value === 'function' ? value(mergedProps) : value,
      ]),
    )
    const clickHandler = ev => {
      ev.preventDefault()
      dispatch(factoryProps.reduxAction)
    }
    return gate(
      factoryProps.requires,
      <ActionComponent
        onClick={clickHandler}
        {...factoryProps}
        {...mergedProps}
      />,
    )
  }

export default actionFactory
